<template>
  <div v-if="image" class="responsive-image">    
    <transition name="fade" :duration="transitionDuration">
      <nuxt-img
        :format="useWebp ? 'webp' : ''"
        :src="useWebp ? image.webpImageSrc : image.resolvedImageSrc"
        :srcset="useWebp ? image.webpComputedSrcset : image.computedSrcset"
        :sizes="dynamicImageSize"
        :alt="image.imageAlt"
        :title="image.imageTitle"
        :width="adjustedImageWidth"
        :height="adjustedImageHeight"
      />
    </transition>
  </div>
</template>

<script setup lang="ts">
import { CMSClient } from '~/api/cmsClient';
import { ref, onMounted, watch, computed, onUnmounted } from 'vue';
import { ICMSImage } from '~/types/index';

const { mediaId, transitionLength } = defineProps({
  mediaId: {
    type: Number,
    required: true,
    default: 164977
  },
  transitionLength: {
    type: Number,
    default: 0
  }
});

const dynamicImageSize = ref('600px'); // default size
// Define variables for adjusted image dimensions
const adjustedImageWidth = ref(600); // Default width
const adjustedImageHeight = ref(338); // Default height, calculated based on 16:9 aspect ratio

const breakpointLargeDesktop = 1800; // pixels
const breakpointDesktop = 1025; // pixels
const breakpointTablet = 480; // pixels

const updateImageSize = () => {
  const viewportWidth = window.innerWidth;

  if (viewportWidth > breakpointLargeDesktop) {
    adjustedImageWidth.value = 4096;
  }
  else if (viewportWidth > breakpointDesktop) {
    adjustedImageWidth.value = 2048;
  }
  else if (viewportWidth > breakpointTablet) {
    adjustedImageWidth.value = 1024;
  } 
  else {
    adjustedImageWidth.value = 600; // Smallest size
  }

  // Update the height based on the 16:9 aspect ratio
  adjustedImageHeight.value = adjustedImageWidth.value / (16/9);

  // Update dynamicImageSize for srcset usage
  dynamicImageSize.value = adjustedImageWidth.value + 'px';
};

const useWebp = true; // Set this constant to true or false based on your preference

const transitionDuration = transitionLength * 1000;
const cmsClient = CMSClient.getInstance();
const image = ref<ICMSImage | null>(null);
const defaultMediaId = 164977; // Fallback mediaId

const ensureHttps = (url: string) => url.replace(/^http:/, 'https:');

const fetchImage = async () => {
  image.value = null;

  if(computedMediaId.value == defaultMediaId)
    console.log("adf fetching fallback image");
  else
    console.log("adf fetchImage with id", computedMediaId.value);
  const response = await cmsClient.fetchImage(computedMediaId.value);
  
  if(useWebp) {
    // Ensure URLs are https
    response.resolvedImageSrc = ensureHttps(response.resolvedImageSrc);
    response.computedSrcset = ensureHttps(response.computedSrcset);
    
    // WebP version
    response.webpImageSrc = ensureHttps(response.resolvedImageSrc.replace(/\.jpg$/, ".jpg.webp").replace(/\.png$/, ".png.webp"));
    
    const webpSrcsetUrls = response.computedSrcset.split(', ').map(srcsetEntry => {
      const [url, width] = srcsetEntry.split(' ');
      const webpUrl = ensureHttps(url).replace(/\.jpg$/, ".jpg.webp").replace(/\.png$/, ".png.webp");
      return `${webpUrl} ${width}`;
    });
    response.webpComputedSrcset = webpSrcsetUrls.join(', ');
  } else {
    // Ensure URLs are https
    response.resolvedImageSrc = ensureHttps(response.resolvedImageSrc);
    response.computedSrcset = ensureHttps(response.computedSrcset);
  }

  image.value = response;
  console.log(image.value);
};

// Computed property to check for valid mediaId and use fallback if necessary
const computedMediaId = computed(() => {
  return (typeof mediaId === 'number' && mediaId) ? mediaId : defaultMediaId;
});


onMounted(() => {
  window.addEventListener('resize', updateImageSize);
  updateImageSize(); // Update size on initial load
  fetchImage(computedMediaId.value);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateImageSize);
});

watch(computedMediaId, (newId) => fetchImage(newId));
</script>



<style lang="scss" scoped>
@import '~/assets/constants.scss';

.responsive-image {
position: relative;
overflow: hidden;
width: 100%;
background-color: $light-white;
}

.responsive-image > img {
display: block;
width: auto;
height: auto;
max-width: 100%;
//margin: 0 auto;
}

/* The starting state of the enter transition */
.fade-enter-from, .fade-leave-to /* .fade-leave-active in <2.1.8 */ {
  opacity: 0;
}

/* The ending state of the enter/leave transition */
.fade-enter-to, .fade-leave-from /* .fade-enter-active in <2.1.8 */ {
  opacity: 1;
}

/* The duration of the transition */
.fade-enter-active, .fade-leave-active {
  transition: opacity 10s;
}
</style>
